/**
 * UTM Tracking Utilities
 * 
 * Captures and persists campaign parameters (UTM) and advertising click IDs (GCLID, etc.)
 * for attribution tracking across the user session.
 */

export interface UTMParams {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
    utm_id?: string;
    gclid?: string; // Google Click ID
    msclkid?: string; // Microsoft Click ID
    li_fat_id?: string; // LinkedIn Click ID
    fbclid?: string; // Facebook Click ID
    [key: string]: string | undefined;
}

const STORAGE_KEY = 'agencyq_utm_params';

// List of supported parameters to scrape from URL
const TRACKED_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_id',
    'gclid',
    'msclkid',
    'li_fat_id',
    'fbclid'
];

/**
 * Parses valid UTM parameters from the current URL search string.
 */
export function getUTMParams(searchParams: URLSearchParams): UTMParams {
    const params: UTMParams = {};

    TRACKED_PARAMS.forEach(key => {
        const value = searchParams.get(key);
        if (value) {
            params[key] = value;
        }
    });

    return params;
}

/**
 * Persists UTM parameters to Session Storage.
 * Strategy: New parameters in the URL overwrite existing session values.
 *           Existing session values are preserved if not present in the new URL.
 */
export function storeUTMParams(currentParams: UTMParams): void {
    if (typeof window === 'undefined') return;

    try {
        const storedJSON = sessionStorage.getItem(STORAGE_KEY);
        const storedParams: UTMParams = storedJSON ? JSON.parse(storedJSON) : {};

        // Merge: Current URL params take precedence over stored params
        const mergedParams = {
            ...storedParams,
            ...currentParams
        };

        // Only write if we have data to store
        if (Object.keys(mergedParams).length > 0) {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(mergedParams));
        }
    } catch (e) {
        console.warn('Failed to store UTM params', e);
    }
}

/**
 * Retrieves the currently active UTM parameters for form submission.
 */
export function getStoredUTMParams(): UTMParams {
    if (typeof window === 'undefined') return {};

    try {
        const storedJSON = sessionStorage.getItem(STORAGE_KEY);
        return storedJSON ? JSON.parse(storedJSON) : {};
    } catch {
        return {};
    }
}
